import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatFullDate = (dateStr) => {
  if (!dateStr) return '---';
  try {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Rome'
    }).format(d);
  } catch (e) {
    return dateStr;
  }
};

const getMovConfig = (qta) => {
  const val = Number(qta) || 0;
  if (val > 0) return { 
    color: 'text-emerald-500', 
    bg: 'bg-emerald-500/10 border-emerald-500/20', 
    icon: 'ArrowUpRight',
    prefix: '+' 
  };
  if (val < 0) return { 
    color: 'text-red-500', 
    bg: 'bg-red-500/10 border-red-500/20', 
    icon: 'ArrowDownRight',
    prefix: '' 
  };
  return { 
    color: 'text-slate-400', 
    bg: 'bg-slate-400/10 border-slate-400/20', 
    icon: 'Minus',
    prefix: '' 
  };
};

// ─── Sotto-componente: Riga Storico ─────────────────────────────────────────

const StoricoRow = ({ row }) => {
  const cfg = getMovConfig(row.qtamovimentata);
  
  return (
    <div className="flex flex-col gap-3 p-5 rounded-[2rem] bg-muted/20 border border-border/40 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl border ${cfg.bg} ${cfg.color}`}>
            <Icon name={cfg.icon} size={14} strokeWidth={3} />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">Data Movimento</span>
            <span className="text-sm font-bold text-foreground/80">{formatFullDate(row.data)}</span>
          </div>
        </div>
        
        <div className="text-right">
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 block">Quantità</span>
          <span className={`text-xl font-black tabular-nums ${cfg.color}`}>
            {cfg.prefix}{row.qtamovimentata}
          </span>
        </div>
      </div>

      <div className="pl-11">
        <div className="p-3 rounded-xl bg-background/50 border border-border/20">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 block mb-1">Nota / Causale</span>
            <p className="text-xs font-bold text-foreground/70 leading-relaxed italic">
                {row.note || row.nota || "Nessuna nota inserita."}
            </p>
        </div>
      </div>
    </div>
  );
};

// ─── Componente principale ───────────────────────────────────────────────────

const StoricoEANModal = ({ ean, nome, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const generatePDF = () => {
    try {
      // Inicijalizacija na 'landscape'
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const tableColumn = ["DATA MOVIMENTO", "QUANTITÀ", "NOTA / CAUSALE"];
      const tableRows = [];

      history.forEach(row => {
        tableRows.push([
          formatFullDate(row.data),
          row.qtamovimentata > 0 ? `+${row.qtamovimentata}` : row.qtamovimentata,
          row.note || row.nota || "---"
        ]);
      });

      // --- HEADER ---
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(37, 99, 235); 
      doc.text("OrizonDesk", 14, 15);
      
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42);
      doc.text("REPORT MOVIMENTI ARTICOLO", 14, 25); // Vraćen naslov

      doc.setDrawColor(226, 232, 240);
      doc.line(14, 28, 283, 28);

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.setFont("helvetica", "bold");
      doc.text("ARTICOLO:", 14, 36);
      doc.setFont("helvetica", "normal");
      doc.text(`${nome || 'N/A'}`, 38, 36);

      doc.setFont("helvetica", "bold");
      doc.text("EAN:", 14, 41);
      doc.setFont("helvetica", "normal");
      doc.text(`${ean}`, 38, 41);

      // --- TABELA ---
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 48,
        margin: { left: 14, right: 14, bottom: 20 },
        theme: 'striped',
        headStyles: { 
          fillColor: [15, 23, 42], 
          textColor: 255, 
          fontSize: 10, 
          fontStyle: 'bold',
          halign: 'center' 
        },
        bodyStyles: { fontSize: 9, cellPadding: 5 },
        columnStyles: {
          1: { halign: 'center', fontStyle: 'bold' }
        },
        // Logika za bojenje kolone Quantità
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 1) {
            const valStr = data.cell.raw.toString();
            if (valStr.startsWith('+')) {
              data.cell.styles.textColor = [16, 185, 129]; // Emerald 500 (Zelena)
            } else if (valStr.startsWith('-')) {
              data.cell.styles.textColor = [239, 68, 68];  // Red 500 (Crvena)
            }
          }
        },
        didDrawPage: function (data) {
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.setFont("helvetica", "normal");
            
            const dateStr = `Data stampa: ${new Date().toLocaleString('it-IT')}`;
            doc.text(dateStr, 14, pageHeight - 10);
            
            const url = "https://desk.orizon-aix.com";
            const textWidth = doc.getTextWidth(url);
            doc.text(url, pageSize.width - textWidth - 14, pageHeight - 10);
        }
      });

      doc.save(`Storico_${ean}_Landscape.pdf`);
      
    } catch (error) {
      console.error("PDF Error:", error);
      alert("Errore tecnico: " + error.message);
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!ean) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('cco_storico')
          .select('*')
          .eq('ean', ean)
          .order('data', { ascending: false });

        if (error) throw error;
        setHistory(data || []);
      } catch (err) {
        console.error("Errore fetch storico:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [ean]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-0 md:p-6 overflow-hidden">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300" onClick={onClose} />

      <div className="relative w-full h-full md:h-auto md:max-h-[85vh] md:max-w-xl bg-card border-t md:border border-border/60 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 duration-300">
        
        {/* Header UI */}
        <div className="shrink-0 px-8 py-6 border-b border-border/40 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-[.3em] text-primary/60">Timeline Movimenti</span>
              </div>
              <h3 className="font-black uppercase tracking-tighter text-foreground text-xl leading-tight break-words whitespace-normal">
                {nome || 'Articolo'}
              </h3>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] font-mono font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    EAN: {ean}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
                <button 
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      generatePDF();
                    }}
                    disabled={history.length === 0}
                    className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 text-primary hover:bg-primary hover:text-white flex items-center justify-center transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Icon name="FileDown" size={20} />
                </button>

                <button onClick={onClose} className="shrink-0 w-10 h-10 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all shadow-sm">
                    <Icon name="X" size={20} />
                </button>
            </div>
          </div>
        </div>

        {/* Content UI */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-hide">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Icon name="Loader2" size={32} className="animate-spin text-primary/40" />
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Caricamento...</span>
            </div>
          ) : history.length > 0 ? (
            history.map((row) => (
              <StoricoRow key={row.id} row={row} />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-border/40 rounded-[2.5rem] bg-muted/5">
                <Icon name="History" size={40} className="text-muted-foreground/20 mb-4" />
                <span className="text-sm font-bold text-muted-foreground/40 uppercase tracking-widest">Nessun movimento</span>
            </div>
          )}
        </div>

        {/* Footer UI */}
        <div className="shrink-0 px-8 py-5 border-t border-border/40 bg-muted/10 flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/30">
                Totale Record: {history.length}
            </span>
            <button 
                type="button"
                onClick={onClose}
                className="px-8 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:bg-border/60 transition-all border border-border/40"
            >
                Chiudi
            </button>
        </div>
      </div>
    </div>
  );
};

export default StoricoEANModal;